import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { Loader2, Filter, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function AuditLogsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const api = useApi();
  
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clinics, setClinics] = useState([]);
  
  // Filters
  const [selectedClinic, setSelectedClinic] = useState('');
  const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadClinics();
    }
  }, []);

  useEffect(() => {
    if (selectedClinic || user?.role === 'clinic_admin') {
      loadLogs();
    }
  }, [selectedClinic]);

  useEffect(() => {
    applyFilters();
  }, [logs, searchQuery, roleFilter, actionFilter]);

  const loadClinics = async () => {
    try {
      const data = await api.get('/clinics/');
      const sorted = data.sort((a, b) => a.name.localeCompare(b.name));
      setClinics(sorted);
      if (sorted.length > 0) {
        setSelectedClinic(sorted[0].id);
      }
    } catch (error) {
      console.error('Failed to load clinics:', error);
      toast.error('Failed to load clinics');
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        clinic_id: user?.role === 'superadmin' ? selectedClinic : user?.clinic_id,
        start_date: startDate,
        end_date: endDate,
        limit: "1000"
      });
      
      const data = await api.get(`/admin/audit-logs?${params.toString()}`);
      const sortedLogs = (data.logs || []).sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      setLogs(sortedLogs);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      toast.error('Failed to load audit logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...logs];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log =>
        (log.user_email && log.user_email.toLowerCase().includes(query)) ||
        (log.action && log.action.toLowerCase().includes(query)) ||
        (log.entity_type && log.entity_type.toLowerCase().includes(query))
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(log => log.role === roleFilter);
    }

    // Action filter
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action === actionFilter);
    }

    setFilteredLogs(filtered);
    setCurrentPage(1);
  };

  const handleApplyDateFilter = () => {
    loadLogs();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const colors = {
      superadmin: 'bg-red-100 text-red-800 border-red-300',
      clinic_admin: 'bg-green-100 text-green-800 border-green-300',
      doctor: 'bg-blue-100 text-blue-800 border-blue-300',
      nurse: 'bg-purple-100 text-purple-800 border-purple-300'
    };
    return (
      <Badge className={`${colors[role] || 'bg-gray-100 text-gray-800'} border`}>
        {role}
      </Badge>
    );
  };

  const getActionBadge = (action) => {
    const actionColors = {
      create: 'bg-green-50 text-green-700 border-green-200',
      update: 'bg-blue-50 text-blue-700 border-blue-200',
      delete: 'bg-red-50 text-red-700 border-red-200',
      login: 'bg-purple-50 text-purple-700 border-purple-200'
    };
    
    const actionType = action?.split('_')[0] || 'other';
    return (
      <Badge className={`${actionColors[actionType] || 'bg-gray-50 text-gray-700 border-gray-200'} border`}>
        {action}
      </Badge>
    );
  };

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentLogs = filteredLogs.slice(startIndex, endIndex);

  const uniqueActions = [...new Set(logs.map(log => log.action))].sort();
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: "#652D90" }}>Audit Logs</h1>
          <p className="text-gray-600 mt-1">Track all system activities and changes</p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          Total: {filteredLogs.length} records
        </Badge>
      </div>

      {/* Filters */}
      <Card className="rounded-2xl border-[#e5e5e5]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: "#652D90" }}>
            <Filter size={20} />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {user?.role === 'superadmin' && (
              <div className="space-y-2">
                <Label>Clinic</Label>
                <Select value={selectedClinic} onValueChange={setSelectedClinic}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {clinics.map(clinic => (
                      <SelectItem key={clinic.id} value={clinic.id}>
                        {clinic.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div className="flex items-end">
              <Button 
                onClick={handleApplyDateFilter} 
                disabled={loading}
                style={{ backgroundColor: "#652D90" }}
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Filter className="mr-2 h-4 w-4" />}
                Apply
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="superadmin">Superadmin</SelectItem>
                  <SelectItem value="clinic_admin">Clinic Admin</SelectItem>
                  <SelectItem value="doctor">Doctor</SelectItem>
                  <SelectItem value="nurse">Nurse</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Action</Label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {uniqueActions.map(action => (
                    <SelectItem key={action} value={action}>{action}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="text"
                  placeholder="Search user, action, entity..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="rounded-2xl border-[#e5e5e5]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle style={{ color: "#652D90" }}>
              Activity Log ({currentLogs.length} of {filteredLogs.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-600">Rows per page:</Label>
              <Select value={itemsPerPage.toString()} onValueChange={(val) => {setItemsPerPage(Number(val)); setCurrentPage(1);}}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#652D90" }} />
            </div>
          ) : currentLogs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No audit logs found for the selected filters
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Timestamp</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Entity Type</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentLogs.map((log, index) => (
                      <TableRow key={index} className="hover:bg-gray-50">
                        <TableCell className="text-sm">{formatDateTime(log.timestamp)}</TableCell>
                        <TableCell className="text-sm font-medium">{log.user_email}</TableCell>
                        <TableCell>{getRoleBadge(log.role)}</TableCell>
                        <TableCell>{getActionBadge(log.action)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.entity_type}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600 max-w-xs truncate">
                          {log.entity_id || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredLogs.length)} of {filteredLogs.length} results
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </Button>
                    <div className="text-sm">
                      Page {currentPage} of {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
    </Layout>
  );
}
